/*
 *                     NOTICE
 *            COPYRIGHT 2023 Ab Initio
 *       UNPUBLISHED - ALL RIGHTS RESERVED
 *
 * This program is confidential and a trade secret of Ab Initio.
 * The receipt or possession of this program does not convey any
 * rights to reproduce or disclose its contents, or to manufacture,
 * use or sell anything that it may describe, in whole or in part,
 * without the specific written consent of Ab Initio.
 */

import { SelectionMode } from "abreact/incubator/ui/SharedTypes";
import { AbKeySet } from "abweb/collection/AbKeySet";
import { AbListHelper } from "abweb/collection/AbListHelper";
import { KeyValue } from "abweb/utils/KeyboardUtils";
import { NumberUtils } from "abweb/utils/NumberUtils";
import { AbEnriched } from "./enrichment/AbEnriched";
import { TypeUtils } from "abweb/utils/TypeUtils";
import { isFocusable as listIsFocusable } from "abreact/input/AbList";
import { AbIterable } from "abweb/collection/AbIterable";
import React from "react";
const isItem = item => {
  return item !== NOT_AN_ITEM;
};
const isFocusable = (item, nonItemsFocusable) => {
  if (item === NOT_AN_ITEM) return nonItemsFocusable ?? false;
  return listIsFocusable(AbEnriched.getEnrichment(item));
};
const isUserSelectable = item => {
  if (item === NOT_AN_ITEM) return false;
  return AbEnriched.isSelectable(AbEnriched.getEnrichment(item));
};
const MAX_ITEMS_SIZE_CTRL_A = 10000;
export const NOT_AN_ITEM = Symbol("selectGroup.notItem");
const clampIndex = (items, index, noWrap) => {
  const maximum = items.size - 1;
  if (!noWrap && index > maximum) return 0;
  if (!noWrap && index < 0) return maximum;
  return NumberUtils.clamp(index, 0, maximum);
};
const findNextFocusableIndex = props => {
  let currIndex = props.startSearchIndex;
  while (true) {
    const nextIndex = clampIndex(props.items, currIndex + props.increment, props.noWrap);
    if (nextIndex === currIndex || nextIndex === props.startSearchIndex) break;
    currIndex = nextIndex;
    if (isFocusable(props.items.itemAt(currIndex), props.nonItemsFocusable)) return nextIndex; // Found an index that is focusable
  }
  return undefined; // Failed to find a next index to go to
};
export const useSelectionGroupHandlers = props => {
  const shiftKeyPivotRef = React.useRef(undefined);
  return getSelectionGroupHandlers({
    ...props,
    pivotRef: shiftKeyPivotRef
  });
};
export const getSelectionGroupHandlers = props => {
  const {
    selectionModeProps,
    keyFunction,
    focusIndexFunction,
    items,
    pivotRef,
    onSelect,
    onUnselect
  } = props;
  const onClick = (event, clickedIndex) => {
    if (props.disabled) return;
    const shift = event.shiftKey;
    const ctrl = event.ctrlKey || event.metaKey; // metaKey is "command" on mac
    anySelectionAction({
      ctrl: ctrl,
      shift: shift,
      event: {
        type: "toggle",
        index: clickedIndex
      },
      focusIndexFunction,
      items,
      keyFunction,
      pivotRef,
      selectionModeProps,
      onSelect,
      onUnselect
    });
  };
  const moveToIndexHelper = (prevIndex, startSearchIndex, increment, shift, ctrl) => {
    const newIndex = findNextFocusableIndex({
      startSearchIndex,
      increment,
      items,
      noWrap: props.selectionModeProps.selectionConfig?.noWrap,
      nonItemsFocusable: props.nonItemsFocusable
    });
    if (newIndex === undefined) return; // Do nothing if no move happened
    anySelectionAction({
      ctrl: ctrl,
      shift: shift,
      event: {
        type: "move",
        fromIndex: prevIndex,
        toIndex: newIndex
      },
      focusIndexFunction,
      items,
      keyFunction,
      pivotRef,
      selectionModeProps,
      nonItemsFocusable: props.nonItemsFocusable,
      orientation: props.orientation,
      onSelect,
      onUnselect
    });
  };
  const onKeyDown = (event, index) => {
    if (items.size === 0 || props.disabled) return;
    const shift = event.shiftKey;
    const ctrl = event.ctrlKey || event.metaKey; // metaKey is "command" on mac
    const verticalOk = props?.orientation !== "horizontal";
    const horizontalOk = props?.orientation !== "vertical";
    switch (event.key) {
      case KeyValue.ARROW_RIGHT:
        if (horizontalOk) {
          moveToIndexHelper(index, index, 1, shift, ctrl);
          event.preventDefault();
        }
        return;
      case KeyValue.ARROW_LEFT:
        if (horizontalOk) {
          moveToIndexHelper(index, index, -1, shift, ctrl);
          event.preventDefault();
        }
        return;
      case KeyValue.ARROW_UP:
        if (verticalOk) {
          moveToIndexHelper(index, index, -1, shift, ctrl);
          event.preventDefault();
        }
        return;
      case KeyValue.ARROW_DOWN:
        if (verticalOk) {
          moveToIndexHelper(index, index, 1, shift, ctrl);
          event.preventDefault();
        }
        return;
      case KeyValue.HOME:
        moveToIndexHelper(index, -1, 1, shift, ctrl);
        event.preventDefault();
        return;
      case KeyValue.END:
        moveToIndexHelper(index, items.size, -1, shift, ctrl);
        event.preventDefault();
        return;
      case KeyValue.SPACE:
      case KeyValue.ENTER:
        anySelectionAction({
          ctrl: ctrl,
          shift: shift,
          event: {
            type: "toggle",
            index
          },
          focusIndexFunction,
          items,
          keyFunction,
          pivotRef,
          selectionModeProps,
          onSelect,
          onUnselect
        });
        event.preventDefault();
        return;
      case "a":
        if (selectionModeProps.selectionMode === SelectionMode.MULTIPLE && ctrl && isItem(items.itemAt(index)) && items.size < MAX_ITEMS_SIZE_CTRL_A) {
          const maybeEnrichedKeyFunction = AbEnriched.createMaybeEnrichedKeyFunction(keyFunction);
          const oldKeySet = AbKeySet.from(selectionModeProps.value, maybeEnrichedKeyFunction);
          const hasUnselectedSelectable = AbIterable.from(items).any(item => isItem(item) && isUserSelectable(item) && !oldKeySet.has(item));
          multiSelectOnChange({
            oldValue: selectionModeProps.value,
            newValue: hasUnselectedSelectable ? AbIterable.from(items).filterType(isItem) : [],
            onChange: selectionModeProps.onChange,
            keyFunction: maybeEnrichedKeyFunction,
            onSelect,
            onUnselect
          });
          event.preventDefault();
        }
        return;
    }
  };
  return {
    onClick,
    onKeyDown
  };
};
const anySelectionAction = props => {
  const enrichedKeyFunction = AbEnriched.createMaybeEnrichedKeyFunction(props.keyFunction);
  switch (props.selectionModeProps.selectionMode) {
    case undefined:
    case SelectionMode.NONE:
      const noSelectionOutput = noSelectionAction({
        items: props.items,
        event: props.event,
        config: props.selectionModeProps.selectionConfig ?? {},
        orientation: props.orientation,
        nonItemsFocusable: props.nonItemsFocusable
      });
      if (noSelectionOutput.focusIndex !== undefined) props.focusIndexFunction(noSelectionOutput.focusIndex);
      break;
    case SelectionMode.SINGLE:
      const singleSelectionOutput = singleSelectionAction({
        items: props.items,
        event: props.event,
        selection: props.selectionModeProps.value,
        config: props.selectionModeProps.selectionConfig ?? {},
        orientation: props.orientation,
        nonItemsFocusable: props.nonItemsFocusable
      });
      if (singleSelectionOutput.focusIndex !== undefined) props.focusIndexFunction(singleSelectionOutput.focusIndex);
      if (singleSelectionOutput.newSelection === NOT_AN_ITEM || TypeUtils.equals(props.selectionModeProps.value, singleSelectionOutput.newSelection, enrichedKeyFunction)) {
        break;
      }
      const {
        item: newItem,
        enrichment: newEnrichment
      } = AbEnriched.extract(singleSelectionOutput.newSelection);
      props.onSelect?.(newItem, newEnrichment);
      if (props.selectionModeProps.value !== undefined) {
        const {
          item,
          enrichment
        } = AbEnriched.extract(props.selectionModeProps.value);
        props.onUnselect?.(item, enrichment);
      }
      props.selectionModeProps.onChange(newItem, singleSelectionOutput.newSelection);
      break;
    case SelectionMode.MULTIPLE:
      const multiSelectionOutput = multiSelectionAction({
        items: props.items,
        event: props.event,
        selection: AbKeySet.from(props.selectionModeProps.value, enrichedKeyFunction),
        config: props.selectionModeProps.selectionConfig ?? {},
        pivot: props.pivotRef.current,
        shift: props.shift,
        ctrl: props.ctrl,
        orientation: props.orientation,
        nonItemsFocusable: props.nonItemsFocusable,
        keyFunction: enrichedKeyFunction
      });
      if (multiSelectionOutput.focusIndex !== undefined) props.focusIndexFunction(multiSelectionOutput.focusIndex);
      if (multiSelectionOutput.newPivot) props.pivotRef.current = {
        index: multiSelectionOutput.newPivot.index,
        selection: AbKeySet.from(multiSelectionOutput.newPivot.newSelection, enrichedKeyFunction)
      };
      if (multiSelectionOutput.newSelection) {
        multiSelectOnChange({
          oldValue: props.selectionModeProps.value,
          newValue: multiSelectionOutput.newSelection,
          keyFunction: enrichedKeyFunction,
          onChange: props.selectionModeProps.onChange,
          onSelect: props.onSelect,
          onUnselect: props.onUnselect
        });
      }
      break;
  }
};
const multiSelectOnChange = props => {
  const unremovable = AbKeySet.from(props.oldValue, props.keyFunction).filter(item => !isUserSelectable(item));
  const newSelectedEnrichedSet = AbKeySet.from(props.newValue, props.keyFunction) // May include unselectable things
  .filter(isUserSelectable) // Remove things that are not selectable
  .union(unremovable) // Add back in unremovable items
  .toSet();
  props.onChange(newSelectedEnrichedSet.map(AbEnriched.getItem), newSelectedEnrichedSet);
  props.onSelect && newSelectedEnrichedSet.forEach(v => {
    if (props.oldValue.has(v)) return;
    const {
      item,
      enrichment
    } = AbEnriched.extract(v);
    props.onSelect?.(item, enrichment);
  });
  props.onUnselect && props.oldValue.forEach(v => {
    if (newSelectedEnrichedSet.has(v)) return;
    const {
      item,
      enrichment
    } = AbEnriched.extract(v);
    props.onUnselect?.(item, enrichment);
  });
};
const noSelectionAction = props => {
  const toIndex = props.event.type === "toggle" ? props.event.index : props.event.toIndex;
  const newItem = props.items.itemAt(toIndex);
  if (newItem === NOT_AN_ITEM) {
    return {
      focusIndex: props.nonItemsFocusable ? toIndex : undefined
    };
  }
  return {
    focusIndex: toIndex
  };
};
const singleSelectionAction = props => {
  const toIndex = props.event.type === "toggle" ? props.event.index : props.event.toIndex;
  const newItem = props.items.itemAt(toIndex);
  if (newItem === NOT_AN_ITEM) {
    // Don't do logic on non-items
    return {
      focusIndex: props?.nonItemsFocusable ? toIndex : undefined,
      newSelection: NOT_AN_ITEM
    };
  }
  if (props.event.type === "toggle") {
    return {
      focusIndex: toIndex,
      newSelection: isUserSelectable(newItem) ? newItem : NOT_AN_ITEM
    };
  }
  return {
    focusIndex: toIndex,
    newSelection: props.config.selectionFollowsFocus && isUserSelectable(newItem) ? newItem : NOT_AN_ITEM
  };
};
const multiSelectionAction = props => {
  const toIndex = props.event.type === "toggle" ? props.event.index : props.event.toIndex;
  const newItem = props.items.itemAt(toIndex);
  if (newItem === NOT_AN_ITEM) {
    // Don't do logic on non-items
    return {
      focusIndex: props?.nonItemsFocusable ? toIndex : undefined
    };
  }
  if (props.config.selectionMode === "few") {
    if (props.ctrl) {
      // ctrl is like many
      const {
        selectionMode,
        selectionFollowsFocus,
        ...rest
      } = props.config;
      return multiSelectionAction({
        ...props,
        ctrl: false,
        config: {
          ...rest,
          selectionMode: "many"
        }
      });
    }
    if (props.event.type === "toggle") {
      // Toggling is like many, with only that item selected/deselected
      if (!isUserSelectable(newItem) && !props.shift) {
        return {
          focusIndex: toIndex
        };
      }
      const {
        selectionMode,
        selectionFollowsFocus,
        ...rest
      } = props.config;
      return multiSelectionAction({
        ...props,
        selection: new AbKeySet(props.selection.has(newItem) ? [newItem] : [], props.keyFunction),
        config: {
          ...rest,
          selectionMode: "many"
        }
      });
    } else {
      const {
        selectionMode,
        selectionFollowsFocus,
        ...rest
      } = props.config;
      if (selectionFollowsFocus && !props.shift) {
        // Selection follows focus is like toggling that item on
        return multiSelectionAction({
          ...props,
          event: {
            type: "toggle",
            index: toIndex
          },
          selection: new AbKeySet([], props.keyFunction)
        });
      }
      // Otherwise, a move is like doing the many case but with no prior selection
      return multiSelectionAction({
        ...props,
        selection: new AbKeySet([], props.keyFunction),
        config: {
          ...rest,
          selectionMode: "many"
        }
      });
    }
  } else {
    if (props.shift) {
      if (props.event.type === "toggle") {
        if (!props.pivot) {
          return multiSelectionAction({
            ...props,
            shift: false
          }); // No pivot is like not having shift active
        } else {
          return multiSelectionAction({
            ...props,
            event: {
              type: "move",
              fromIndex: props.pivot.index,
              toIndex: props.event.index
            }
          }); // With pivot is like a shift move
        }
      } else {
        const fromIndex = props.pivot?.index ?? props.event.fromIndex;
        const slice = AbListHelper.filterType(props.items.slice(Math.min(fromIndex, toIndex), Math.max(fromIndex, toIndex) + 1), isItem).filter(isUserSelectable);
        return {
          newSelection: props.selection.difference(props.pivot?.selection ?? []).union(slice),
          newPivot: {
            index: fromIndex,
            newSelection: slice
          },
          focusIndex: toIndex
        };
      }
    } else {
      // Shift not active
      if (props.event.type === "toggle") {
        if (props.selection.has(newItem)) {
          return {
            newSelection: props.selection.difference([newItem]),
            newPivot: {
              index: toIndex,
              newSelection: []
            },
            focusIndex: toIndex
          };
        } else {
          return {
            newSelection: props.selection.union([newItem]),
            newPivot: {
              index: toIndex,
              newSelection: [newItem]
            },
            focusIndex: toIndex
          };
        }
      } else {
        // No op. Just moving to the index.
        return {
          focusIndex: toIndex
        };
      }
    }
  }
};
//# sourceMappingURL=AbSelectGroup.js.map
